import { conditionalAutoScroll, throttledStreamScroll } from "./scrollHelpers";
import useStreamingStateStore from "../store/useStreamingStateStore";
import useGenerateApiSignal from "../store/useGenerateApiSignal";
import QUERYKEYS, { chatModelInfoQueryKey } from "../api/queryKeys";
import useFeedbackStore from "../store/useFeedbackStore";
import { DENIAL_ERROR_MESSAGES } from "./helpers";

/**
 * Shared streaming utility for handling ReadableStream responses.
 * Used by both generate and regenerate flows to avoid code duplication.
 */

// Regex to extract SSE_START:{...}:SSE_END markers from any chunk.
// Uses non-greedy match to handle multiple markers in one buffer.
const SSE_MARKER_RE = /SSE_START:(.*?):SSE_END/g;

/**
 * Extract all SSE events from a chunk and return remaining text content.
 * Events are wrapped as SSE_START:{"event":"TYPE","data":{...}}:SSE_END
 * so they can never be confused with LLM-generated content.
 *
 * @param {string} chunk - Raw decoded chunk from stream
 * @returns {{ events: Array<{event: string, data: any}>, text: string, parts: Array<Object> }}
 */
function extractSSEEvents(chunk) {
  const events = [];
  const parts = [];
  let cursor = 0;
  let match;

  // Reset lastIndex for global regex
  SSE_MARKER_RE.lastIndex = 0;
  while ((match = SSE_MARKER_RE.exec(chunk)) !== null) {
    if (match.index > cursor) {
      parts.push({ type: 'text', value: chunk.slice(cursor, match.index) });
    }
    try {
      const event = JSON.parse(match[1]);
      events.push(event);
      parts.push({ type: 'event', value: event });
    } catch {
      // Malformed payload — skip
    }
    cursor = SSE_MARKER_RE.lastIndex;
  }

  if (cursor < chunk.length) {
    parts.push({ type: 'text', value: chunk.slice(cursor) });
  }

  // Strip all markers and SSE comment lines (keepalive) to get remaining text content
  const text = chunk.replace(SSE_MARKER_RE, "").replace(/^:.*\n?\n?/gm, "");
  return { events, text, parts };
}

/**
 * Dispatch a single parsed SSE event into the streaming store and invoke callbacks.
 * This is the single source of truth for all event handling — used by both the live
 * stream path (processStreamingResponse) and the reconnect/replay path (attachReconnectStream).
 *
 * Returns a terminal signal when the event ends or pauses the generation:
 *   'complete' | 'failed' | 'cancelled' | 'awaiting_approval' | 'approval_card' | null
 *
 * Note: 'approval_card' is not a true terminal (generation continues waiting for the user)
 * but requires the caller to preserve generate_id for the sidebar breathing dot.
 *
 * @param {Object} evt              - Parsed SSE event { event, data }
 * @param {string|null} messageId   - Streaming store key (promptId)
 * @param {Object} callbacks        - { onApprovalCard, onModelUsed, onGenerationComplete, onContentSnapshot }
 * @returns {string|null}
 */
export function dispatchStreamEvent(evt, messageId, callbacks = {}) {
  const { onApprovalCard, onModelUsed, onGenerationComplete, onContentSnapshot } = callbacks;

  switch (evt.event) {
    case 'GENERATION_STARTED': {
      const genId = evt.data?.generation_id;
      if (genId) {
        // generation_id is tracked per-message in useStreamingStateStore for reconnect.
        if (messageId) useStreamingStateStore.getState().setGenerationId(messageId, genId);
      }
      return null;
    }
    case 'CURSOR': {
      if (messageId) {
        const seq = evt.data?.seq;
        if (seq != null) useStreamingStateStore.getState().setLatestSeq(messageId, seq);
      }
      return null;
    }
    case 'APPROVAL_CARD': {
      if (onApprovalCard) onApprovalCard(evt.data);
      return 'approval_card';
    }
    case 'PROVIDER_CONNECTION_REQUIRED': {
      if (messageId) {
        useStreamingStateStore.getState().setConnectionRequirement(messageId, evt.data);
      }
      return null;
    }
    case 'STATUS': {
      if (messageId) useStreamingStateStore.getState().setStatusText(messageId, evt.data?.message || evt.data);
      return null;
    }
    case 'TOOL_START': {
      if (messageId) useStreamingStateStore.getState().addToolCall(messageId, evt.data);
      return null;
    }
    case 'TOOL_END': {
      if (messageId) useStreamingStateStore.getState().resolveToolCall(messageId, evt.data);
      return null;
    }
    case 'MODEL_USED': {
      if (onModelUsed) onModelUsed(evt.data?.model_id);
      return null;
    }
    case 'ACTIVITY_ITEM': {
      if (messageId) useStreamingStateStore.getState().addActivityItem(messageId, evt.data);
      return null;
    }
    case 'GENERATION_COMPLETE': {
      if (onGenerationComplete) {
        // Live stream path: pass final_content alongside result so the callback
        // can write the confirmed answer to cache BEFORE flipping isStreaming=false.
        // completeStreaming ownership transfers to the callback to eliminate the
        // one-frame race between the streaming gate opening and cache settling.
        onGenerationComplete(evt.data?.result ?? null, evt.data?.final_content ?? null);
      } else {
        // Reconnect path (attachReconnectStream passes no onGenerationComplete):
        // no cache write needed here, flip isStreaming as today.
        if (messageId) useStreamingStateStore.getState().completeStreaming(messageId);
      }
      return 'complete';
    }
    case 'GENERATION_FAILED': {
      if (messageId) useStreamingStateStore.getState().failStreaming(messageId);
      if (evt.data?.error_code && typeof evt.data?.error === 'string') {
        useFeedbackStore.getState().showFeedback(evt.data.error, 'error');
      }
      return 'failed';
    }
    case 'generation_limit_reached': {
      if (messageId) useStreamingStateStore.getState().failStreaming(messageId);
      useFeedbackStore.getState().showFeedback(DENIAL_ERROR_MESSAGES.generation_limit_reached(), "error");
      return 'failed';
    }
    case 'GENERATION_CANCELLED': {
      if (messageId) useStreamingStateStore.getState().failStreaming(messageId);
      return 'cancelled';
    }
    case 'GENERATION_AWAITING_APPROVAL': {
      if (messageId) useStreamingStateStore.getState().completeStreaming(messageId);
      return 'awaiting_approval';
    }
    case 'ANSWER_USAGE': {
      if (callbacks.onUsage) callbacks.onUsage(evt.data);
      return null;
    }
    case 'GENERATION_EVENT': {
      // Reconnect cursor-replay path: unwrap persisted event payloads.
      // Token text is not replayed — the database listener provides final content.
      const { seq, event_type, payload = {} } = evt.data || {};
      if (seq != null && messageId) useStreamingStateStore.getState().setLatestSeq(messageId, seq);
      const normalizedEventType = event_type?.toLowerCase();
      if (normalizedEventType === 'tool_start' && messageId) {
        useStreamingStateStore.getState().addToolCall(messageId, payload);
      }
      if (normalizedEventType === 'tool_end' && messageId) {
        useStreamingStateStore.getState().resolveToolCall(messageId, payload);
      }
      if (normalizedEventType === 'activity_item' && messageId) {
        useStreamingStateStore.getState().addActivityItem(messageId, { ...payload, seq });
      }
      if (normalizedEventType === 'content_snapshot') {
        // Mid-stream answer checkpoint persisted by the worker. Lets a refresh
        // mid-generation render partial answer text instead of a blank bubble.
        // Caller (attachRecoveryStream) wires onContentSnapshot to write the
        // snapshot into the React Query cache; live streams leave it unset.
        onContentSnapshot?.(payload, seq);
      }
      return null;
    }
    default:
      return null;
  }
}

/**
 * Process a streaming response from the backend.
 * 
 * @param {ReadableStreamDefaultReader} reader - The stream reader from fetch response
 * @param {Object} options - Configuration options
 * @param {Function} options.onChunk - Callback for each chunk: (accumulatedContent) => void
 * @param {Function} options.onComplete - Callback when streaming completes
 * @param {Function} options.onError - Callback for errors: (error) => void
 * @param {Function} options.onApprovalCard - Callback for APPROVAL_CARD events: (cardData) => void
 * @param {boolean} options.autoScroll - Whether to auto-scroll during streaming (default: true)
 * @param {string} options.messageId - Message ID (promptId) for tracking streaming state (recommended)
 * @param {boolean} options.preserveActivity - Preserve prior Activity for approval resume
 * @param {Array} options.initialActivityItems - Persisted Activity used to seed approval resume
 * @returns {Promise<string>} - The complete accumulated content
 */
export async function processStreamingResponse(reader, options = {}) {
  const {
    onChunk,
    onComplete,
    onError,
    onApprovalCard,
    onModelUsed,
    onGenerationComplete,
    onUsage,
    autoScroll = true,
    messageId,
    preserveActivity = false,
    initialActivityItems = [],
  } = options;

  const decoder = new TextDecoder("utf-8");
  let answerCont = "";
  let first = true;
  let streamHadContent = false;
  let pendingBuffer = ""; // Buffer for partial SSE_START markers split across reads
  let activeRound = null;
  // Track whether an approval card SSE event was received on this stream.
  // When it was, the stream closes with no text content (by design) — we must
  // not call failStreaming so the streaming store stays clean and doesn't
  // interfere with the approval card display.
  let _approvalCardFired = false;
  // Set when a terminal SSE event (GENERATION_COMPLETE/FAILED/CANCELLED) arrives
  // mid-stream. Prevents failStreaming from firing at stream-end for the media path
  // which sends GENERATION_COMPLETE before closing the stream with no text content.
  let _terminalReceived = false;

  // Mark streaming as started
  if (messageId) {
    console.debug(`[STREAM] startStreaming: ${messageId} at ${Date.now()}`);
    const streamingStore = useStreamingStateStore.getState();
    if (preserveActivity) {
      streamingStore.resumeStreaming(messageId, initialActivityItems);
    } else {
      streamingStore.startStreaming(messageId);
    }
  }

  const _streamStart = Date.now();
  let _firstChunkAt = null;
  let _firstActivityAt = null;
  let _chunkCount = 0;

  try {
    for (let done = false; !done;) {
      const { value, done: readerDone } = await reader.read();
      done = readerDone;

      if (value) {
        const rawChunk = decoder.decode(value, { stream: true });
        
        // Skip backend completion markers
        if (rawChunk === "</END>") {
          continue;
        }

        // Prepend any partial marker leftover from previous read
        const chunk = pendingBuffer + rawChunk;
        pendingBuffer = "";

        // If chunk contains an unclosed SSE_START (no matching :SSE_END),
        // buffer everything from the last SSE_START onward for the next read.
        let chunkToProcess = chunk;
        const lastStart = chunk.lastIndexOf("SSE_START:");
        if (lastStart !== -1) {
          const hasEnd = chunk.indexOf(":SSE_END", lastStart) !== -1;
          if (!hasEnd) {
            // Partial marker — buffer it, process everything before it
            pendingBuffer = chunk.slice(lastStart);
            chunkToProcess = chunk.slice(0, lastStart);
            if (!chunkToProcess) continue;
          }
        }

        // Extract SSE_START/SSE_END wrapped events, leaving only text content
        const { parts } = extractSSEEvents(chunkToProcess);

        // Handle extracted SSE events via the shared dispatcher. Defer
        // GENERATION_COMPLETE until this read's text has been written: browsers
        // may coalesce the final text bytes and terminal marker into one read,
        // and completion carries authoritative final_content.
        // Timing debug is kept local here since it needs the _streamStart context.
        const completionEvents = [];
        for (const part of parts) {
          if (part.type === 'text') {
            const text = part.value.replace(/^:.*\n?\n?/gm, "");
            if (!text) continue;

            answerCont += text;
            streamHadContent = true;
            _chunkCount++;
            if (!_firstChunkAt) {
              _firstChunkAt = Date.now();
              console.debug(`[STREAM] First text chunk at +${_firstChunkAt - _streamStart}ms (firstActivity was +${_firstActivityAt ? _firstActivityAt - _streamStart : 'none'}ms)`);
            }

            if (messageId && !useStreamingStateStore.getState().streamHadContent(messageId)) {
              useStreamingStateStore.getState().setStreamHasContent(messageId);
            }

            if (onChunk) onChunk(answerCont);

            if (first && autoScroll) {
              first = false;
              setTimeout(() => conditionalAutoScroll(true, true), 100);
            }
            if (autoScroll) throttledStreamScroll(answerCont, 100, true);
            continue;
          }

          const evt = part.value;
          if (evt.event === "STATUS") {
            console.debug(`[STREAM] STATUS "${evt.data?.message || evt.data}" at +${Date.now() - _streamStart}ms`);
          } else if (evt.event === "TOOL_START") {
            console.debug(`[STREAM] TOOL_START "${evt.data?.name}" at +${Date.now() - _streamStart}ms`);
          } else if (evt.event === "TOOL_END") {
            console.debug(`[STREAM] TOOL_END "${evt.data?.name}" at +${Date.now() - _streamStart}ms`);
          } else if (evt.event === "ACTIVITY_ITEM") {
            if (!_firstActivityAt) {
              _firstActivityAt = Date.now();
              console.debug(`[STREAM] First ACTIVITY_ITEM type="${evt.data?.type}" at +${_firstActivityAt - _streamStart}ms`);
            } else {
              console.debug(`[STREAM] ACTIVITY_ITEM type="${evt.data?.type}" seq=${evt.data?.seq} at +${Date.now() - _streamStart}ms`);
            }
          }
          if (evt.event === 'ROUND_START') {
            activeRound = {
              id: evt.data?.round_id,
              answerStart: answerCont.length,
            };
          }
          if (evt.event === 'ROUND_BECAME_TOOL') {
            if (activeRound && activeRound.id === evt.data?.round_id) {
              answerCont = answerCont.slice(0, activeRound.answerStart);
              // The round's text was narration, not the answer — an emptied
              // bubble must go back to the skeleton, not keep the writing spinner.
              if (messageId && !answerCont) {
                useStreamingStateStore.getState().setStreamHasContent(messageId, false);
              }
              if (onChunk) onChunk(answerCont);
            }
            activeRound = null;
          }
          if (evt.event === 'ROUND_BECAME_FINAL') {
            activeRound = null;
          }
          if (evt.event === 'GENERATION_COMPLETE') {
            completionEvents.push(evt);
            continue;
          }
          const terminal = dispatchStreamEvent(evt, messageId, { onApprovalCard, onModelUsed, onGenerationComplete, onUsage });
          if (terminal === 'approval_card') _approvalCardFired = true;
          if (terminal !== null && terminal !== 'approval_card') _terminalReceived = true;
        }

        for (const evt of completionEvents) {
          dispatchStreamEvent(evt, messageId, { onApprovalCard, onModelUsed, onGenerationComplete, onUsage });
          _terminalReceived = true;
        }
      }
    }

    // Call completion callback
    if (onComplete) {
      onComplete(answerCont);
    }

    // Mark streaming as successfully completed, or clean up if no content arrived.
    //
    // stillPending guards against the ADK early-ack pattern (and any other
    // case where the HTTP stream closes before a real terminal SSE event
    // arrives): the initial POST can return — closing this reader — while
    // the agent keeps running server-side and GENERATION_STARTED already
    // registered this messageId in pendingGenerations. Without this check,
    // reaching stream-end here with content already received would call
    // completeStreaming() and mark the generation "done" even though it
    // isn't — silently hiding the beaming indicator and freezing the
    // Activity/tool-call list while the backend keeps working in the
    // background, with no visible sign of progress until the user
    // refreshes (which re-fetches the by-then-finished Firestore doc).
    // pendingGenerations is already cleared by completeStreaming/failStreaming
    // themselves whenever a real terminal SSE event was handled inline
    // during the loop above, so this is a no-op for the normal path.
    if (messageId) {
      const stillPending = !!useStreamingStateStore.getState().pendingGenerations[messageId];
      if (stillPending) {
        console.debug(`[STREAM] stream ended but ${messageId} is still pending (no terminal event received) — leaving streaming state intact for reconnect`);
      } else if (streamHadContent) {
        console.debug(`[STREAM] completeStreaming: ${messageId} after ${_chunkCount} chunks, total +${Date.now() - _streamStart}ms`);
        useStreamingStateStore.getState().completeStreaming(messageId);
      } else if (!_approvalCardFired && !_terminalReceived) {
        // Stream ended with no content (e.g. tab suspended before first chunk) —
        // clean up so handleAddMessage isn't permanently blocked for this messageId.
        // Skip when an approval card or terminal SSE event was already received —
        // the stream closes intentionally with no text content in those cases.
        useStreamingStateStore.getState().failStreaming(messageId);
      }
    }

    return answerCont;
  } catch (error) {
    if (messageId) {
      const streamingStore = useStreamingStateStore.getState();
      if (error.name === 'AbortError' && streamingStore.pendingGenerations[messageId]) {
        streamingStore.detachStreaming(messageId);
      } else {
        streamingStore.failStreaming(messageId);
      }
    }
    
    // Call error callback
    if (onError) {
      onError(error);
    }
    throw error;
  }
}

/**
 * Update message content in React Query cache during streaming for generate flow.
 * Creates message structure if it doesn't exist (first chunk), then updates answer content.
 * 
 * @param {QueryClient} queryClient - React Query client instance
 * @param {string} chatId - Chat ID
 * @param {string} spaceId - Space ID (optional)
 * @param {string} promptId - The message ID to update
 * @param {string} content - The streaming content to set
 */
/**
 * Patch the model_id on the current answer in the React Query cache.
 * Called when a MODEL_USED SSE event arrives at stream start.
 *
 * @param {QueryClient} queryClient
 * @param {string} chatId
 * @param {string} spaceId
 * @param {string} promptId
 * @param {string} modelId
 */
export function updateMessageModelId(queryClient, chatId, spaceId, promptId, modelId) {
  if (!modelId || !queryClient) return;
  queryClient.setQueryData([QUERYKEYS.CHAT_MESSAGES, chatId, spaceId], (oldData) => {
    if (!oldData) return oldData;
    const messages = oldData.messages;
    const msgIdx = messages.findIndex((m) => m.id === promptId);
    if (msgIdx === -1) return oldData;
    const message = messages[msgIdx];
    if (!message?.answers) return oldData;
    const currentIndex = message.currentIndex ?? 0;
    const updatedAnswers = message.answers.slice();
    updatedAnswers[currentIndex] = { ...updatedAnswers[currentIndex], model_id: modelId };
    const updatedMessages = messages.slice();
    updatedMessages[msgIdx] = { ...message, answers: updatedAnswers };
    return { ...oldData, messages: updatedMessages };
  });
}

export function syncPersistedChatModelCache(queryClient, chatId, spaceId, modelId) {
  if (!modelId || !chatId || !queryClient) return;
  const modelInfoKey = chatModelInfoQueryKey(chatId, spaceId);
  queryClient.setQueryData(modelInfoKey, (old) =>
    old
      ? {
          ...old,
          model_id: modelId,
          persisted_model_id: modelId,
        }
      : old
  );
}

export function updateAnswerUsage(queryClient, chatId, spaceId, messageId, answerId, usage) {
  queryClient.setQueryData([QUERYKEYS.CHAT_MESSAGES, chatId, spaceId], (oldData) => {
    if (!oldData) return oldData;
    return {
      ...oldData,
      messages: oldData.messages.map((message) => {
        if (message.id !== messageId) return message;
        // Find by answer ID (regenerate/edit flow) or fall back to currentIndex (generate flow)
        const answerIdx = answerId ? message.answers.findIndex(a => a.id === answerId) : -1;
        const targetIdx = answerIdx !== -1 ? answerIdx : (message.currentIndex ?? 0);
        // Also patch id with the backend UUID so delete works correctly even before
        // the post-generation invalidateQueries refetch replaces the optimistic frontend UUID.
        const idPatch = answerId ? { id: answerId } : {};
        return {
          ...message,
          answers: message.answers.map((a, i) => i === targetIdx ? { ...a, ...idPatch, usage } : a),
        };
      }),
    };
  });
}

export function updateGenerateMessage(queryClient, chatId, spaceId, promptId, content) {
  queryClient.setQueryData([QUERYKEYS.CHAT_MESSAGES, chatId, spaceId], (oldData) => {
    if (!oldData) return oldData;
    
    const messages = oldData.messages;
    const msgIdx = messages.findIndex((m) => m.id === promptId);
    if (msgIdx === -1) return oldData;

    const message = messages[msgIdx];
    if (!message?.answers) return oldData;

    const currentIndex = message.currentIndex ?? 0;
    const updatedAnswers = message.answers.map((answer, idx) =>
      idx === currentIndex ? { ...answer, content } : answer
    );

    const updatedMessages = messages.slice();
    updatedMessages[msgIdx] = { ...message, answers: updatedAnswers };

    return { ...oldData, messages: updatedMessages };
  });
}

/**
 * Update message content in React Query cache during streaming for regenerate flow.
 * Updates a specific answer by ID with new content.
 * 
 * @param {QueryClient} queryClient - React Query client instance
 * @param {string} chatId - Chat ID
 * @param {string} spaceId - Space ID (optional)
 * @param {string} messageId - The message ID containing the answer
 * @param {string} answerId - The specific answer ID to update
 * @param {string} content - The streaming content to set
 */
export function updateRegenerateMessage(queryClient, chatId, spaceId, messageId, answerId, content) {
  queryClient.setQueryData([QUERYKEYS.CHAT_MESSAGES, chatId, spaceId], (oldData) => {
    if (!oldData) return oldData;
    
    return {
      ...oldData,
      messages: oldData.messages.map((message) => {
        if (message.id === messageId) {
          const answerIdx = message.answers.findIndex((a) => a.id === answerId);
          if (answerIdx === -1) return message;
          const updatedAnswers = message.answers.slice();
          updatedAnswers[answerIdx] = { ...updatedAnswers[answerIdx], content };
          return { ...message, answers: updatedAnswers };
        }
        return message;
      })
    };
  });
}

/**
 * Append error message to answer content in React Query cache.
 * 
 * @param {QueryClient} queryClient - React Query client instance
 * @param {string} chatId - Chat ID
 * @param {string} spaceId - Space ID (optional)
 * @param {string} promptId - The message ID to update
 * @param {string} errorMessage - The error message to append
 */
/**
 * Creates a throttled buffer that accumulates streaming content and flushes
 * to the provided update function on a timer interval. Reduces the frequency
 * of React Query cache mutations (and therefore React re-renders + paints)
 * from one-per-token down to ~20/sec, which is imperceptible to users.
 *
 * @param {Function} updateFn - Cache update function to call with latest content
 * @param {number} intervalMs - Flush interval in milliseconds (default: 50)
 * @returns {{ push: Function, flush: Function, destroy: Function }}
 */
export function createChunkBuffer(updateFn, intervalMs = 16) {
  let latestContent = null;
  let dirty = false;

  const intervalId = setInterval(() => {
    if (dirty) {
      updateFn(latestContent);
      dirty = false;
    }
  }, intervalMs);

  return {
    push(content) {
      latestContent = content;
      dirty = true;
    },
    flush() {
      if (dirty) {
        updateFn(latestContent);
        dirty = false;
      }
    },
    discard() {
      dirty = false;
      latestContent = null;
    },
    destroy() {
      clearInterval(intervalId);
    },
  };
}

/**
 * Run a streaming response with the canonical message-stream wiring used by every
 * caller (generate, regenerate, edit prompt, approval flow). Centralises:
 *   - chunkBuffer (50ms) creation/destroy (skip with buffered=false)
 *   - approvalCard flag → onComplete cleanup gating
 *   - "stillPending" check (ADK early-ack) → defer cleanup until reconnect terminal event
 *   - GENERATION_COMPLETE cleanup + media-path direct write of `result`
 *   - APPROVAL_CARD pause + caller hook
 *   - cleanup method selector (loadingHandlers.cleanup vs stopLoading)
 *
 * Replaces ~50–80 lines of near-identical wiring at each call site. Adding a new
 * concern (e.g. additional terminal SSE handling) now requires editing one place.
 *
 * @param {Object} args
 * @param {ReadableStreamDefaultReader} args.reader      - HTTP response stream reader
 * @param {string} args.messageId                        - prompt_id used to track streaming state
 * @param {Object} args.loadingHandlers                  - from createMessageLoadingHandlers
 * @param {Function} args.writeChunk                     - (content) => void; writes content to cache
 * @param {Function} [args.writeModel]                   - (modelId) => void; called on MODEL_USED
 * @param {Function} [args.onApprovalCard]               - (data) => void; caller-supplied
 * @param {Function} [args.onChunkBefore]                - (content) => void; runs before buffer.push
 * @param {Function} [args.onCompleteAfter]              - () => void; runs at end of onComplete
 * @param {Function} [args.onError]                      - (error) => void
 * @param {'cleanup'|'stopLoading'} [args.cleanupMethod] - which loadingHandlers method to call
 * @param {boolean} [args.buffered]                      - wrap writeChunk in 50ms chunkBuffer
 * @param {boolean} [args.checkStillPending]             - skip cleanup if pendingGenerations[messageId] still set.
 *                                                         Only set true for ADK early-ack paths where the initial
 *                                                         stream closes intentionally without a terminal event and
 *                                                         a reconnect stream delivers the terminal event instead.
 *                                                         All other callers (media, text, edit) must use false so
 *                                                         a proxy/Cloud Run idle-timeout drop does not leave the
 *                                                         loading UI permanently stuck.
 * @param {Function} [args.onPendingStreamEnd]            - called when the response closes while
 *                                                         the durable generation is still pending.
 * @param {boolean} [args.autoScroll]                    - forwarded to processStreamingResponse
 * @param {boolean} [args.preserveActivity]              - preserve prior Activity for approval resume
 * @param {Array} [args.initialActivityItems]             - persisted Activity used to seed approval resume
 */
export async function runMessageStream({
  reader,
  messageId,
  loadingHandlers,
  writeChunk,
  writeModel,
  onApprovalCard,
  onChunkBefore,
  onCompleteAfter,
  onError,
  onUsage,
  cleanupMethod = 'cleanup',
  buffered = true,
  checkStillPending = false,
  onPendingStreamEnd,
  autoScroll = true,
  preserveActivity = false,
  initialActivityItems = [],
}) {
  let approvalCardReceived = false;

  const buffer = buffered
    ? createChunkBuffer(writeChunk)
    : { push: writeChunk, flush: () => {}, discard: () => {}, destroy: () => {} };
  let previousContent = '';

  const doCleanup = () => {
    if (cleanupMethod === 'stopLoading') {
      loadingHandlers?.stopLoading?.();
    } else {
      loadingHandlers?.cleanup?.();
    }
  };

  try {
    await processStreamingResponse(reader, {
      messageId,
      preserveActivity,
      initialActivityItems,
      onChunk: (content) => {
        if (onChunkBefore) onChunkBefore(content);
        buffer.push(content);
        if (content.length < previousContent.length) buffer.flush();
        previousContent = content;
      },
      onModelUsed: writeModel ? (modelId) => writeModel(modelId) : undefined,
      onUsage,
      onGenerationComplete: (result, finalContent) => {
        // GENERATION_COMPLETE is the terminal signal — release the loading UI
        // immediately rather than waiting for the SSE stream to physically close
        // (the backend keeps the response open for _persist_answer_background,
        // which can leave the beaming dot stuck for several seconds).
        // When authoritative final content is present, do not publish a pending
        // accumulated snapshot first. Tool-driven code streams can contain a
        // malformed penultimate snapshot that must never reach a renderer as
        // the streaming gate opens.
        if (finalContent) buffer.discard();
        else buffer.flush();
        // Media path: GENERATION_COMPLETE carries the rendered content (e.g.
        // "![STORAGE_PATH](...)") so write it before _persist_answer_background
        // reaches Firestore.
        if (result) writeChunk(result);
        // Text path: write final confirmed answer from GENERATION_COMPLETE payload
        // so the cache holds the complete content BEFORE isStreaming flips false.
        // Eliminates the one-frame race where the renderer gate opens with a
        // partial/stale cache value.
        if (finalContent) writeChunk(finalContent);
        // Flip isStreaming AFTER both cache writes — zero race window.
        if (messageId) useStreamingStateStore.getState().completeStreaming(messageId);
        doCleanup();
      },
      onComplete: () => {
        buffer.flush();
        // Skip cleanup when:
        //   1. APPROVAL_CARD was just emitted — generate_id must remain set so
        //      the sidebar breathing dot stays visible while the user decides.
        //   2. ADK early-ack pattern — initial POST returns immediately while the
        //      agent is still running; cleanup waits for the reconnect-stream
        //      terminal event. Detected via pendingGenerations[messageId].
        const stillPending = checkStillPending
          && !!useStreamingStateStore.getState().pendingGenerations[messageId];
        if (!approvalCardReceived && !stillPending) {
          doCleanup();
        }
        if (!approvalCardReceived && stillPending && onPendingStreamEnd) {
          const pending = useStreamingStateStore.getState().pendingGenerations[messageId];
          onPendingStreamEnd({
            generationId: pending.generation_id,
            afterSeq: pending.latest_seq,
          });
        }
        if (onCompleteAfter) onCompleteAfter();
      },
      onApprovalCard: (data) => {
        approvalCardReceived = true;
        buffer.flush();
        loadingHandlers?.pauseForApproval?.();
        if (onApprovalCard) onApprovalCard(data);
      },
      onError,
      autoScroll,
    });
  } finally {
    buffer.destroy();
  }
}

export function addGenerateErrorMessage(queryClient, chatId, spaceId, promptId, errorMessage) {
  queryClient.setQueryData([QUERYKEYS.CHAT_MESSAGES, chatId, spaceId], (oldData) => {
    if (!oldData) return oldData;
    
    return {
      ...oldData,
      messages: oldData.messages.map((message) => {
        if (message.id === promptId) {
          if (message && message.answers) {
            const currentIndex = message.currentIndex ?? 0;
            return {
              ...message,
              answers: message.answers.map((answer, idx) =>
                idx === currentIndex
                  ? { ...answer, content: answer.content + errorMessage, isError: true }
                  : answer
              )
            };
          }
          return message;
        }
        return message;
      })
    };
  });
}

/**
 * Attach a cursor-based reconnect stream for a generation that is still running.
 *
 * Opens GET /spark/generation/{id}/stream?after_seq={afterSeq}, replays persisted
 * GENERATION_EVENT payloads into the streaming store, and calls the appropriate
 * completion callback when a terminal SSE event arrives.
 *
 * Returns an AbortController so the caller can cancel the stream on unmount.
 *
 * @param {string} generationId   - The generation record ID.
 * @param {string} messageId      - The promptId / streaming store key.
 * @param {number} afterSeq       - Resume from this cursor position (default 0).
 * @param {Object} options
 * @param {string} options.apiUrl          - Backend base URL.
 * @param {Function} options.onComplete    - Called on GENERATION_COMPLETE.
 * @param {Function} options.onFailed      - Called on GENERATION_FAILED SSE event.
 * @param {Function} options.onCancelled   - Called on GENERATION_CANCELLED SSE event.
 * @param {Function} options.onStreamError - Called when the SSE connection itself
 *                                           drops before any terminal event arrives.
 *                                           Distinct from onFailed — the generation
 *                                           may still be running on the backend.
 *                                           Use this to start the polling fallback.
 * @returns {AbortController}
 */
export function attachReconnectStream(generationId, messageId, afterSeq = 0, options = {}) {
  const { apiUrl, chat_id, space_id, onComplete, onFailed, onCancelled, onAwaitingApproval, onStreamError, onContentSnapshot } = options;
  const abortController = new AbortController();

  (async () => {
    useStreamingStateStore.getState().startStreaming(messageId);

    try {
      const sessionToken = localStorage.getItem('session_token') || '';
      const url = new URL(`${apiUrl}/spark/generation/${generationId}/stream`);
      url.searchParams.set('after_seq', afterSeq);
      if (chat_id) url.searchParams.set('chat_id', chat_id);
      if (space_id) url.searchParams.set('space_id', space_id);
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: { 'X-Session-Token': sessionToken },
        signal: abortController.signal,
      });

      if (!response.ok || !response.body) {
        // HTTP-level failure — connection could not be established.
        // Do not mark as failed: generation may still be running on the backend.
        // Detach the streaming state and let the caller decide (poll fallback).
        useStreamingStateStore.getState().detachStreaming(messageId);
        onStreamError?.();
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let pendingBuffer = '';

      for (let done = false; !done;) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;

        if (value) {
          const rawChunk = decoder.decode(value, { stream: true });
          const chunk = pendingBuffer + rawChunk;
          pendingBuffer = '';

          // Buffer partial SSE_START markers
          const lastStart = chunk.lastIndexOf('SSE_START:');
          let chunkToProcess = chunk;
          if (lastStart !== -1) {
            const hasEnd = chunk.indexOf(':SSE_END', lastStart) !== -1;
            if (!hasEnd) {
              pendingBuffer = chunk.slice(lastStart);
              chunkToProcess = chunk.slice(0, lastStart);
              if (!chunkToProcess) continue;
            }
          }

          const { events } = extractSSEEvents(chunkToProcess);

          for (const evt of events) {
            const terminal = dispatchStreamEvent(evt, messageId, { onContentSnapshot });
            if (terminal === 'complete') { onComplete?.(); return; }
            if (terminal === 'awaiting_approval') { onAwaitingApproval?.(evt.data?.approval_id); return; }
            if (terminal === 'cancelled') { onCancelled?.(); return; }
            if (terminal === 'failed') { onFailed?.(); return; }
          }
        }
      }

      // Stream ended without a terminal event — connection dropped mid-stream.
      // Do not mark as failed: generation may still be running on the backend.
      // Detach and hand off to the polling fallback.
      useStreamingStateStore.getState().detachStreaming(messageId);
      onStreamError?.();
    } catch (err) {
      if (err.name === 'AbortError') return; // intentional cancel — no cleanup needed
      // Unexpected error (network failure, etc.) — same as connection drop.
      useStreamingStateStore.getState().detachStreaming(messageId);
      onStreamError?.();
    }
  })();

  return abortController;
}

// Terminal statuses returned by GET /generation/{id}
const _TERMINAL_STATUSES = new Set(['complete', 'stopped', 'failed', 'interrupted']);

// Exponential backoff delays (ms) for poll request failures
const _BACKOFF_DELAYS = [500, 5000, 10000, 20000, 30000];

// Maximum time (ms) to poll before surfacing a "stalled" signal.
// 300 s (5 min) — presentation generation (plan + images + PPTX assembly) can
// legitimately take 3-5 minutes. The 60 s original fired "Still working on it"
// mid-generation, which looked like a hang even though progress was normal.
const _WATCHDOG_MS = 300_000;

// Hard stop (ms) — if still no terminal status after this long, give up and
// stop polling entirely rather than continuing forever. Matches the 600s
// (10 min) "stuck" threshold already used in SingleMessage.js's isStuck
// auto-dismiss, so once onGaveUp clears the generate signal, the existing
// isEmptyAwaitingRecovery + isStuck fallback (Stop/Dismiss UI) takes over
// without any new UI needing to be built. Without this, a genuinely orphaned
// generation (backend worker crashed, no proactive server-side sweep
// guarantees a terminal status — see repo memory) would poll forever with
// no way for the frontend to ever resolve the stuck skeleton on its own.
const _GIVE_UP_MS = 600_000;

/**
 * Poll GET /generation/{id} until a terminal status is reached, the watchdog
 * fires, or the hard stop is reached (whichever comes first for each stage).
 *
 * Used as a fallback when the SSE reconnect stream drops before delivering a
 * terminal event. Implements exponential backoff on request failures, a
 * 300 s watchdog that fires onStalled if no terminal status is seen, and a
 * 600 s hard stop that fires onGaveUp and stops polling entirely.
 *
 * @param {string} generationId  - Generation record ID.
 * @param {Object} options
 * @param {string}   options.apiUrl      - Backend base URL.
 * @param {Function} options.onComplete  - Called when status is "complete".
 * @param {Function} options.onFailed    - Called when status is "failed"/"interrupted".
 * @param {Function} options.onCancelled - Called when status is "stopped".
 * @param {Function} options.onStalled   - Called once after 300 s with no terminal status.
 * @param {Function} options.onGaveUp    - Called once after 600 s with no terminal status; polling stops after this.
 * @returns {AbortController} - Abort to stop polling (e.g. on component unmount).
 */
export function pollGenerationStatus(generationId, options = {}) {
  const { apiUrl, chat_id, space_id, onComplete, onFailed, onCancelled, onStalled, onGaveUp } = options;
  const abortController = new AbortController();
  const signal = abortController.signal;
  const statusUrl = new URL(`${apiUrl}/spark/generation/${generationId}`);
  if (chat_id) statusUrl.searchParams.set('chat_id', chat_id);
  if (space_id) statusUrl.searchParams.set('space_id', space_id);

  (async () => {
    const startedAt = Date.now();
    let backoffIndex = 0;   // index into _BACKOFF_DELAYS for request failures
    let stalledFired = false;

    const delay = (ms) => new Promise((resolve, reject) => {
      const t = setTimeout(resolve, ms);
      signal.addEventListener('abort', () => { clearTimeout(t); reject(new DOMException('AbortError', 'AbortError')); }, { once: true });
    });

    while (!signal.aborted) {
      try {
        const sessionToken = localStorage.getItem('session_token') || '';
        const response = await fetch(statusUrl.toString(), {
          method: 'GET',
          headers: { 'X-Session-Token': sessionToken },
          signal,
        });

        if (!response.ok) {
          // HTTP error — treat as a request failure and back off
          throw new Error(`status ${response.status}`);
        }

        const data = await response.json();
        const status = data?.status;

        // Reset backoff on a successful response
        backoffIndex = 0;

        if (_TERMINAL_STATUSES.has(status)) {
          if (status === 'complete') {
            onComplete?.();
          } else if (status === 'stopped') {
            onCancelled?.();
          } else {
            // failed or interrupted
            onFailed?.();
          }
          return;
        }

        // Fire watchdog once if still running past the threshold
        if (!stalledFired && (Date.now() - startedAt) >= _WATCHDOG_MS) {
          stalledFired = true;
          onStalled?.();
        }

        // Hard stop — give up and stop polling entirely rather than continuing
        // forever. A genuinely orphaned generation would otherwise poll forever
        // with nothing to ever clear the stuck UI.
        if ((Date.now() - startedAt) >= _GIVE_UP_MS) {
          onGaveUp?.();
          return;
        }

        // Still running — poll again in 5 seconds
        await delay(5000);

      } catch (err) {
        if (err.name === 'AbortError') return; // intentional cancel

        if (!stalledFired && (Date.now() - startedAt) >= _WATCHDOG_MS) {
          stalledFired = true;
          onStalled?.();
        }
        if ((Date.now() - startedAt) >= _GIVE_UP_MS) {
          onGaveUp?.();
          return;
        }

        // Request failed — apply exponential backoff
        const waitMs = _BACKOFF_DELAYS[Math.min(backoffIndex, _BACKOFF_DELAYS.length - 1)];
        backoffIndex = Math.min(backoffIndex + 1, _BACKOFF_DELAYS.length - 1);
        try {
          await delay(waitMs);
        } catch {
          return; // aborted during backoff wait
        }
      }
    }
  })();

  return abortController;
}

/**
 * Attach to a running generation and wire up the standard recovery lifecycle:
 *   - SSE reattach (resume from afterSeq)
 *   - Poll fallback if the SSE stream drops before a terminal event
 *   - Uniform terminal handling (clear generate signal, drop loading flag,
 *     reset status, invalidate the chat-messages cache)
 *   - "Awaiting approval" handling (clear signal + loading without invalidate)
 *
 * Shared by useReconnectStream (in-memory state intact) and useRefreshRecovery
 * (in-memory state wiped after page refresh).
 *
 * @param {Object} args
 * @param {string}   args.generationId
 * @param {string}   args.messageId
 * @param {number}   [args.afterSeq=0]   - SSE replay cursor; 0 = full replay.
 * @param {string}   args.apiUrl
 * @param {Object}   args.queryClient    - React Query client for cache invalidation.
 * @param {string}   args.chat_id
 * @param {string}   [args.space_id]
 * @param {Function} args.setIsLatestMessageLoading
 * @param {Function} args.setGenerateStatus
 * @returns {() => void} cleanup — abort the SSE reattach and any poll fallback.
 */
export function attachRecoveryStream({
  generationId,
  messageId,
  afterSeq = 0,
  apiUrl,
  queryClient,
  chat_id,
  space_id,
  setIsLatestMessageLoading,
  setGenerateStatus,
}) {
  const controllers = { reattach: null, poll: null };

  // Track the last applied snapshot seq to prevent rewind. The replay endpoint
  // returns events ordered ascending, so this normally just monotonically
  // increases — guard exists for safety in case backend ever re-emits.
  let lastSnapshotSeq = -1;

  const clearGenerateSignal = () => {
    useGenerateApiSignal.getState().removeSignal(chat_id);
  };

  const onContentSnapshot = (payload, seq) => {
    const text = payload?.answer;
    if (typeof text !== 'string' || !text) return;
    if (seq != null && seq <= lastSnapshotSeq) return; // older snapshot, ignore
    lastSnapshotSeq = seq ?? lastSnapshotSeq;
    // answer.id == generation_id (1:1 mapping by design in the backend).
    updateRegenerateMessage(queryClient, chat_id, space_id, messageId, generationId, text);
    useStreamingStateStore.getState().setStreamHasContent(messageId);
  };

  const finalize = () => {
    clearGenerateSignal();
    setIsLatestMessageLoading(false);
    setGenerateStatus(null);
    // Clear the pendingGenerations entry so useReconnectStream doesn't try to
    // reattach on the next page visit. completeStreaming/failStreaming handle
    // this for the SSE path (via dispatchStreamEvent), but the poll fallback
    // path bypasses those — this call covers it.
    useStreamingStateStore.getState().clearPendingGeneration(messageId);
    useStreamingStateStore.getState().clearStopping(messageId);
    // NOTE: Single cache invalidation. The backend persist-race fix
    // (spark_generation_orchestrator._emit_terminal_event awaits the persist
    // task before flipping status) guarantees Firestore has the answer content
    // by the time this terminal event reaches us, so a single refetch is
    // sufficient under normal conditions.
    //
    // Deferred — FE retry safety net: if Firestore replica lag ever produces
    // a terminal event with empty content in production, wrap this invalidate
    // in a "refetch → check empty → retry up to N times with backoff" loop.
    // Not added now because the BE fix closes the race at the source; this
    // would only be defense-in-depth against eventual-consistency edge cases.
    queryClient.invalidateQueries({ queryKey: [QUERYKEYS.CHAT_MESSAGES, chat_id, space_id] });
  };

  const startPollFallback = () => {
    setGenerateStatus('reconnecting');
    controllers.poll = pollGenerationStatus(generationId, {
      apiUrl,
      chat_id,
      space_id,
      onComplete: finalize,
      onFailed: finalize,
      onCancelled: finalize,
      onStalled: () => setGenerateStatus('stalled'),
      // Give up after the hard-stop threshold: clear the signal and stop the
      // loading UI (but don't invalidate the cache — there's no new content,
      // invalidating would just refetch the same stale/empty answer). Once the
      // signal clears, SingleMessage's existing isEmptyAwaitingRecovery +
      // isStuck fallback ("Generation was interrupted" + Stop/Dismiss) takes
      // over on its own — no new UI needed for this to resolve gracefully.
      onGaveUp: () => {
        clearGenerateSignal();
        useStreamingStateStore.getState().clearPendingGeneration(messageId);
        useStreamingStateStore.getState().clearStopping(messageId);
        setIsLatestMessageLoading(false);
        setGenerateStatus(null);
      },
    });
  };

  controllers.reattach = attachReconnectStream(generationId, messageId, afterSeq, {
    apiUrl,
    chat_id,
    space_id,
    onComplete: finalize,
    onFailed: finalize,
    onCancelled: finalize,
    onStreamError: startPollFallback,
    onContentSnapshot,
    onAwaitingApproval: () => {
      // Generation paused for approval — clear the dot and spinner.
      // useApprovalFlow renders the approval card from Firestore independently.
      clearGenerateSignal();
      useStreamingStateStore.getState().clearPendingGeneration(messageId);
      useStreamingStateStore.getState().clearStopping(messageId);
      setIsLatestMessageLoading(false);
      setGenerateStatus(null);
    },
  });

  // attachReconnectStream's startStreaming(messageId) call above wipes any
  // activityItems (fresh reset). The generation-event replay it just kicked
  // off never carries ACTIVITY_ITEM/TOOL_START/TOOL_END — only content
  // snapshots — so without this the trail stays blank for the rest of the
  // generation even though the DB already has it (mid-stream ArrayUnion
  // writes). Seed it from cache now.
  const cachedMessage = queryClient
    .getQueryData?.([QUERYKEYS.CHAT_MESSAGES, chat_id, space_id])
    ?.messages?.find((m) => m.id === messageId);
  const cachedAnswer =
    cachedMessage?.answers?.find((a) => a.id === generationId);
  if (cachedAnswer?.activity?.length) {
    useStreamingStateStore.getState().hydrateActivityItems(messageId, cachedAnswer.activity);
  }

  setIsLatestMessageLoading(true);
  setGenerateStatus('reconnecting');

  return () => {
    controllers.reattach?.abort();
    controllers.poll?.abort();
  };
}
